import type { SVGProps } from 'react';

/**
 * Jeu d'icônes.
 *
 * ── Pourquoi les dessiner ici plutôt qu'installer une bibliothèque ──────────
 * Lucide ou Heroicons apporteraient mille icônes pour les vingt utilisées, et
 * une dépendance de plus à suivre. Surtout : une bibliothèque tierce impose son
 * style de trait, et il faudrait de toute façon la corriger.
 *
 * ── Un seul style, sans exception ───────────────────────────────────────────
 * Trait de 1,5 px, extrémités et jointures arrondies, grille de 24, aucun
 * remplissage. C'est la cohérence du trait — bien plus que le dessin de chaque
 * pictogramme — qui distingue une interface soignée d'un assemblage. Mélanger
 * un jeu plein et un jeu filaire se remarque immédiatement, même sans savoir
 * pourquoi.
 *
 * ── Toujours décoratives ────────────────────────────────────────────────────
 * `aria-hidden` par défaut : une icône accompagne un texte, elle ne le
 * remplace jamais. Un bouton réduit à une icône porte son `aria-label`, et le
 * lecteur d'écran lit ce label — pas le SVG.
 */

export interface ProprietesIcone extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Taille en pixels. 16 dans un bouton, 18 en navigation, 20 en titre. */
  taille?: number;
}

function Svg({ taille = 18, children, ...reste }: ProprietesIcone & { children: React.ReactNode }) {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      // `shrink-0` : dans un bouton en flexbox, une icône sans cette contrainte
      // s'écrase quand le libellé est long — elle devient un ovale.
      className="shrink-0"
      {...reste}
    >
      {children}
    </svg>
  );
}

// ── Navigation ───────────────────────────────────────────────────────────────

export const IconeAccueil = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" /></Svg>
);

export const IconeJournal = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H5.5A1.5 1.5 0 0 1 4 18.5z" /><path d="M4 17.5A1.5 1.5 0 0 1 5.5 16H20" /><path d="M8 7h8M8 11h5" /></Svg>
);

/**
 * Fiche de préparation : une feuille au coin corné.
 *
 * Voisine du cahier journal dans le menu, elle ne doit pas lui ressembler :
 * le journal est un livre relié, la fiche une feuille volante qu'on sort du
 * classeur — et le coin corné est ce qui la distingue d'un simple document.
 */
export const IconeFichePreparation = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 12.5h6M9 16.5h4" /></Svg>
);

/**
 * Agenda : un carnet à reliure.
 *
 * Distincte du cahier journal — dont l'icône est un livre ouvert — parce que
 * les deux se côtoient dans le menu et qu'on ne doit pas cliquer l'un en
 * croyant ouvrir l'autre. Les anneaux de la reliure font la différence.
 */
export const IconeAgenda = (p: ProprietesIcone) => (
  <Svg {...p}><rect x="4" y="4" width="16" height="17" rx="2" /><path d="M8 2v3M16 2v3" /><path d="M8 11h8M8 15h5" /></Svg>
);

export const IconeEleves = (p: ProprietesIcone) => (
  <Svg {...p}><circle cx="9" cy="8" r="3.2" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 5.3a3.2 3.2 0 0 1 0 5.4" /><path d="M17.5 14.2A6.5 6.5 0 0 1 21.5 20" /></Svg>
);

export const IconeEmploiDuTemps = (p: ProprietesIcone) => (
  <Svg {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="M8 14h.01M12 14h.01M16 14h.01M8 17.5h.01M12 17.5h.01" /></Svg>
);

export const IconeProgressions = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M3 20h18" /><path d="M6 20v-5M11 20v-9M16 20v-6M21 20V7" /></Svg>
);

export const IconeProgrammation = (p: ProprietesIcone) => (
  <Svg {...p}><rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="4" rx="1.5" /><rect x="3" y="14" width="7" height="6" rx="1.5" /><rect x="14" y="11" width="7" height="9" rx="1.5" /></Svg>
);

export const IconeEvaluation = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M9 11.5 11 13.5l4.5-4.5" /><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 3v2h6V3" /></Svg>
);

export const IconeProgramme = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H12v18H6.5A2.5 2.5 0 0 1 4 18.5z" /><path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H12v18h5.5a2.5 2.5 0 0 0 2.5-2.5z" /></Svg>
);

/**
 * Les 108 heures : un sablier.
 *
 * Une horloge aurait dit « l'heure qu'il est » ; un sablier dit « le temps
 * qu'il reste », ce qui est exactement le propos du module.
 */
export const IconeHeures = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M6 2h12" /><path d="M6 22h12" /><path d="M6 2v4.5a6 6 0 0 0 3.5 5.5A6 6 0 0 0 6 17.5V22" /><path d="M18 2v4.5a6 6 0 0 1-3.5 5.5 6 6 0 0 1 3.5 5.5V22" /></Svg>
);

export const IconeReglages = (p: ProprietesIcone) => (
  <Svg {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></Svg>
);

export const IconeEquipe = (p: ProprietesIcone) => (
  <Svg {...p}><circle cx="8.5" cy="9" r="3" /><circle cx="17" cy="10" r="2.4" /><path d="M2.5 19a6 6 0 0 1 12 0" /><path d="M15.5 15.2A5 5 0 0 1 21.5 19" /></Svg>
);

export const IconeSecurite = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M12 3 5 6v5.5c0 4.3 2.9 8.2 7 9.5 4.1-1.3 7-5.2 7-9.5V6z" /><path d="m9.5 12 1.8 1.8 3.5-3.6" /></Svg>
);

export const IconeAbonnement = (p: ProprietesIcone) => (
  <Svg {...p}><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19" /><path d="M6.5 14.5h3" /></Svg>
);

// ── Actions ──────────────────────────────────────────────────────────────────

export const IconePlus = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M12 5v14M5 12h14" /></Svg>
);

export const IconeCrayon = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7.5 18.5 3 20l1.5-4.5z" /></Svg>
);

export const IconeCorbeille = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M4 6h16" /><path d="M9.5 6V4.5A1.5 1.5 0 0 1 11 3h2a1.5 1.5 0 0 1 1.5 1.5V6" /><path d="M6 6v13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6" /><path d="M10 11v5M14 11v5" /></Svg>
);

/** Télécharger : une flèche qui descend dans un bac. */
export const IconeTelecharger = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></Svg>
);

export const IconeImprimer = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M7 8V3h10v5" /><path d="M7 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" /><rect x="7" y="14" width="10" height="7" rx="1" /></Svg>
);

/** Plein écran : quatre coins qui s'écartent. */
export const IconePleinEcran = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M4 9V5a1 1 0 0 1 1-1h4" /><path d="M15 4h4a1 1 0 0 1 1 1v4" /><path d="M20 15v4a1 1 0 0 1-1 1h-4" /><path d="M9 20H5a1 1 0 0 1-1-1v-4" /></Svg>
);

/** Quitter le plein écran : les mêmes coins, rentrés. */
export const IconeQuitterPleinEcran = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M9 4v4a1 1 0 0 1-1 1H4" /><path d="M20 9h-4a1 1 0 0 1-1-1V4" /><path d="M15 20v-4a1 1 0 0 1 1-1h4" /><path d="M4 15h4a1 1 0 0 1 1 1v4" /></Svg>
);

export const IconeImport = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M12 3v12" /><path d="m7.5 10.5 4.5 4.5 4.5-4.5" /><path d="M3.5 17.5V19a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-1.5" /></Svg>
);

export const IconeDupliquer = (p: ProprietesIcone) => (
  <Svg {...p}><rect x="8.5" y="8.5" width="12" height="12" rx="2" /><path d="M15.5 5.5V5a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h.5" /></Svg>
);

export const IconeCheck = (p: ProprietesIcone) => (
  <Svg {...p}><path d="m4.5 12.5 5 5 10-11" /></Svg>
);

export const IconeCroix = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M6 6l12 12M18 6 6 18" /></Svg>
);

export const IconeChevronGauche = (p: ProprietesIcone) => (
  <Svg {...p}><path d="m15 5-7 7 7 7" /></Svg>
);

export const IconeChevronDroite = (p: ProprietesIcone) => (
  <Svg {...p}><path d="m9 5 7 7-7 7" /></Svg>
);

export const IconeChevronBas = (p: ProprietesIcone) => (
  <Svg {...p}><path d="m5 9 7 7 7-7" /></Svg>
);

export const IconeRecherche = (p: ProprietesIcone) => (
  <Svg {...p}><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 4.5 4.5" /></Svg>
);

/** Œil ouvert : afficher un mot de passe. */
export const IconeOeil = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></Svg>
);

/** Œil barré : masquer de nouveau un mot de passe. */
export const IconeOeilBarre = (p: ProprietesIcone) => (
  <Svg {...p}>
    <path d="M9.9 5.2A10.7 10.7 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-2.9 3.9M6.6 6.6C3.7 8.4 2 12 2 12s3.5 7 10 7a10 10 0 0 0 5.4-1.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
  </Svg>
);

export const IconeCalendrier = (p: ProprietesIcone) => (
  <Svg {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Svg>
);

export const IconeAlerte = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M12 3.5 22 20H2z" /><path d="M12 9.5v5M12 17.5h.01" /></Svg>
);

export const IconeInfo = (p: ProprietesIcone) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></Svg>
);

export const IconeSucces = (p: ProprietesIcone) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="m8.5 12.2 2.4 2.4 4.6-5" /></Svg>
);

export const IconeMenu = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M4 7h16M4 12h16M4 17h16" /></Svg>
);

export const IconeDeconnexion = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M9.5 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.5" /><path d="M16 16.5 20.5 12 16 7.5" /><path d="M20.5 12H9.5" /></Svg>
);

export const IconeVide = (p: ProprietesIcone) => (
  <Svg {...p}><path d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5z" /><path d="M4 8.5 12 13l8-4.5M12 13v7" /></Svg>
);

/** Table d'icônes par identifiant de module, pour la navigation. */
/** Documentation : un livre ouvert. */
export function IconeDocumentation(p: ProprietesIcone) {
  return (
    <Svg {...p}>
      <path d="M2 4h6a3 3 0 0 1 3 3v11a2.5 2.5 0 0 0-2.5-2H2z" />
      <path d="M22 4h-6a3 3 0 0 0-3 3v11a2.5 2.5 0 0 1 2.5-2H22z" />
    </Svg>
  );
}

/** Support : une bulle de conversation. */
export function IconeSupport(p: ProprietesIcone) {
  return (
    <Svg {...p}>
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.8-.9L3 21l1.9-5a8.4 8.4 0 0 1-.9-3.8 8.4 8.4 0 0 1 8.4-9 8.4 8.4 0 0 1 8.6 8.3z" />
    </Svg>
  );
}

export const ICONES_MODULE: Record<string, (p: ProprietesIcone) => React.JSX.Element> = {
  accueil: IconeAccueil,
  journal: IconeJournal,
  fiches: IconeFichePreparation,
  agenda: IconeAgenda,
  eleves: IconeEleves,
  edt: IconeEmploiDuTemps,
  progressions: IconeProgressions,
  programmation: IconeProgrammation,
  evaluation: IconeEvaluation,
  heures: IconeHeures,
  programme: IconeProgramme,
  reglages: IconeReglages,
  equipe: IconeEquipe,
  securite: IconeSecurite,
  abonnement: IconeAbonnement,
  support: IconeSupport,
  documentation: IconeDocumentation,
  impressions: IconeImprimer,
};
